import React, { useEffect, useState } from 'react';
import { Box, Paper, Typography, TextField, Button, Alert, CircularProgress, Divider, Switch, FormControlLabel, MenuItem, Select, InputLabel, FormControl, Tabs, Tab, Table, TableHead, TableRow, TableCell, TableBody, Dialog, DialogTitle, DialogContent, DialogActions } from '@mui/material';
import { styled } from '@mui/material/styles';
import apiService from '../services/api';
import ReactMemo from 'react';

const Container = styled(Box)(({ theme }) => ({
  maxWidth: 1100,
  margin: '24px auto',
  padding: '0 16px'
}));

const Card = styled(Paper)(({ theme }) => ({
  padding: 24
}));

const Settings = () => {
  const defaultMenuSettings = { dashboard: true, archive: true, elevations: true, reports: true, buildings: true, settings: true };
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [groupId, setGroupId] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [groups, setGroups] = useState([]);
  const [users, setUsers] = useState([]);
  const [gName, setGName] = useState('');
  const [gDesc, setGDesc] = useState('');
  const [gLoading, setGLoading] = useState(false);
  const [gError, setGError] = useState('');
  const [gSuccess, setGSuccess] = useState('');
  const [menuSettings, setMenuSettings] = useState(() => {
    try {
      const s = localStorage.getItem('menu_settings');
      return s ? { ...defaultMenuSettings, ...JSON.parse(s) } : defaultMenuSettings;
    } catch {
      return defaultMenuSettings;
    }
  });
  const [tab, setTab] = useState('menu');
  const [defectTypes, setDefectTypes] = useState([]);
  const [newDefectType, setNewDefectType] = useState('');
  const [newDefectDescription, setNewDefectDescription] = useState('');
  const [defectEditOpen, setDefectEditOpen] = useState(false);
  const [editingDefectIndex, setEditingDefectIndex] = useState(null);
  const [editingDefectName, setEditingDefectName] = useState('');
  const [editingDefectDescription, setEditingDefectDescription] = useState('');
  const [annexList, setAnnexList] = useState([]);
  const [newAnnexTitle, setNewAnnexTitle] = useState('');
  
  useEffect(() => {
    const loadAnnexMaster = async () => {
      try {
        const resp = await apiService.listAnnexMaster();
        setAnnexList(resp.items || []);
      } catch (e) {
        console.error('Failed to load annex master, falling back to localStorage', e);
        try {
          const raw = localStorage.getItem('annex_list');
          const arr = raw ? JSON.parse(raw) : [];
          if (Array.isArray(arr)) {
            setAnnexList(arr.map((t, i) => ({ id: i + 1, title: t, file_url: null })));
          }
        } catch {}
      }
    };
    loadAnnexMaster();
  }, []);

  useEffect(() => {
    const loadGroups = async () => {
      try {
        const res = await apiService.getGroups();
        setGroups(res || []);
      } catch (e) {
        setGroups([]);
      }
    };
    loadGroups();
  }, []);

  useEffect(() => {
    const loadUsers = async () => {
      try {
        const res = await apiService.getUsers();
        setUsers(res || []);
      } catch (e) {
        setUsers([]);
      }
    };
    if (tab === 'users') {
      loadUsers();
    }
  }, [tab]);

  const openDefectEditor = (idx) => {
    const item = defectTypes[idx] || {};
    setEditingDefectIndex(idx);
    setEditingDefectName(item.name || '');
    setEditingDefectDescription(item.description || '');
    setDefectEditOpen(true);
  };

  const closeDefectEditor = () => {
    setDefectEditOpen(false);
    setEditingDefectIndex(null);
    setEditingDefectName('');
    setEditingDefectDescription('');
  };

  const applyDefectEditor = () => {
    if (editingDefectIndex === null) return;
    const name = String(editingDefectName || '').trim();
    if (!name) return;
    setDefectTypes((prev) => prev.map((item, idx) => (
      idx === editingDefectIndex
        ? { ...item, name, description: String(editingDefectDescription || '').trim() }
        : item
    )));
    closeDefectEditor();
  };

  useEffect(() => {
    const loadDefectTypes = async () => {
      try {
        const resp = await apiService.getDefectTypes();
        const next = (resp?.items || [])
          .map((it) => ({
            id: it?.id,
            name: String(it?.name || '').trim(),
            description: String(it?.description || '').trim(),
          }))
          .filter((it) => !!it.name);
        setDefectTypes(next);
      } catch (e) {
        setError(e?.message || 'Failed to load defect types');
      }
    };
    if (tab === 'defects') {
      loadDefectTypes();
    }
  }, [tab]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    if (!name.trim() || !email.trim() || !password.trim()) {
      setError('All fields are required');
      return;
    }
    try {
      setLoading(true);
      await apiService.registerUser({ email, name, password, group_id: groupId ? Number(groupId) : null });
      setSuccess('User created successfully');
      setName('');
      setEmail('');
      setPassword('');
      setGroupId('');
    } catch (err) {
      setError(err?.message || 'Failed to create user');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateGroup = async (e) => {
    e.preventDefault();
    setGError('');
    setGSuccess('');
    if (!gName.trim()) {
      setGError('Group name is required');
      return;
    }
    try {
      setGLoading(true);
      const res = await apiService.createGroup({ name: gName.trim(), description: gDesc.trim() });
      setGSuccess('Group created');
      setGName('');
      setGDesc('');
      setGroups((prev) => Array.isArray(prev) ? [...prev, res] : [res]);
    } catch (err) {
      setGError(err?.message || 'Failed to create group');
    } finally {
      setGLoading(false);
    }
  };

  const saveMenuSettings = () => {
    try {
      localStorage.setItem('menu_settings', JSON.stringify(menuSettings));
      setSuccess('Menu settings saved');
    } catch (e) {
      setError('Failed to save menu settings');
    }
  };

  const isPdfUrl = (value) => /\.pdf($|\?)/i.test(String(value || ''));

  return (
    <Container>
      <Typography variant="h5" sx={{ mb: 2 }}>Settings</Typography>
      <Tabs value={tab} onChange={(e, v) => setTab(v)} sx={{ mb: 2 }}>
        <Tab label="Menu" value="menu" />
        <Tab label="Groups" value="groups" />
        <Tab label="Users" value="users" />
        <Tab label="Defect Types" value="defects" />
        <Tab label="Annex List" value="annex" />
      </Tabs>
      {tab === 'menu' && (
      <Card elevation={3} sx={{ mb: 3 }}>
        <Typography variant="h6" sx={{ mb: 2 }}>Menu Settings</Typography>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mb: 2 }}>
          <FormControlLabel control={<Switch checked={menuSettings.dashboard} onChange={(e) => setMenuSettings(s => ({ ...s, dashboard: e.target.checked }))} />} label="Show Dashboard" />
          <FormControlLabel control={<Switch checked={menuSettings.archive} onChange={(e) => setMenuSettings(s => ({ ...s, archive: e.target.checked }))} />} label="Show Archive" />
          <FormControlLabel control={<Switch checked={menuSettings.elevations} onChange={(e) => setMenuSettings(s => ({ ...s, elevations: e.target.checked }))} />} label="Show Image Manager" />
          <FormControlLabel control={<Switch checked={menuSettings.reports} onChange={(e) => setMenuSettings(s => ({ ...s, reports: e.target.checked }))} />} label="Show Reports" />
          <FormControlLabel control={<Switch checked={menuSettings.settings} onChange={(e) => setMenuSettings(s => ({ ...s, settings: e.target.checked }))} />} label="Show Settings" />
        </Box>
        <Button variant="contained" onClick={saveMenuSettings}>Save Menu Settings</Button>
      </Card>
      )}
      {tab === 'users' && (
      <Card elevation={3} sx={{ mb: 3 }}>
        <Typography variant="h6" sx={{ mb: 2 }}>Create New User</Typography>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}
        <Box component="form" onSubmit={handleSubmit}>
          <TextField
            label="Full Name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            fullWidth
            sx={{ mb: 2 }}
          />
          <TextField
            label="Email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            fullWidth
            sx={{ mb: 2 }}
          />
          <TextField
            label="Password"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            fullWidth
            sx={{ mb: 2 }}
          />
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel id="group-id-label">User Group</InputLabel>
            <Select labelId="group-id-label" label="User Group" value={groupId} onChange={(e) => setGroupId(e.target.value)}>
              <MenuItem value="">None</MenuItem>
              {(groups || []).map((g) => (
                <MenuItem key={g.id} value={String(g.id)}>{g.name}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <Button type="submit" variant="contained" disabled={loading}>
            {loading ? <CircularProgress size={20} color="inherit" /> : 'Create User'}
          </Button>
        </Box>
        <Divider sx={{ my: 3 }} />
        <Typography variant="h6" sx={{ mb: 2 }}>Users</Typography>
        <Box sx={{ width: '100%', overflowX: 'auto' }}>
        <Table size="small" sx={{ tableLayout: 'fixed', minWidth: 900 }}>
          <TableHead>
            <TableRow>
              <TableCell sx={{ width: 72 }}>ID</TableCell>
              <TableCell sx={{ width: 180 }}>Name</TableCell>
              <TableCell sx={{ width: 280 }}>Email</TableCell>
              <TableCell sx={{ width: 180 }}>Group</TableCell>
              <TableCell sx={{ width: 140 }}>Status</TableCell>
              <TableCell sx={{ width: 160 }} align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {(users || []).map((u) => {
              const g = (groups || []).find((gg) => gg.id === u.group_id);
              return (
                <TableRow key={u.id}>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>{u.id}</TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{u.name}</TableCell>
                  <TableCell sx={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{u.email}</TableCell>
                  <TableCell>
                    <Select size="small" value={u.group_id || ''} onChange={(e) => setUsers(prev => prev.map(x => x.id === u.id ? { ...x, group_id: e.target.value ? Number(e.target.value) : null } : x))} sx={{ minWidth: 140 }}>
                      <MenuItem value="">None</MenuItem>
                      {(groups || []).map((gg) => (
                        <MenuItem key={gg.id} value={String(gg.id)}>{gg.name}</MenuItem>
                      ))}
                    </Select>
                  </TableCell>
                  <TableCell>
                    <FormControlLabel control={<Switch checked={!!u.is_active} onChange={(e) => setUsers(prev => prev.map(x => x.id === u.id ? { ...x, is_active: e.target.checked } : x))} />} label={u.is_active ? 'Active' : 'Inactive'} />
                  </TableCell>
                  <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                    <Button size="small" variant="outlined" sx={{ mr: 1 }} onClick={async () => {
                      try {
                        const payload = { group_id: u.group_id || null, is_active: !!u.is_active };
                        const saved = await apiService.updateUser(u.id, payload);
                        setUsers(prev => prev.map(x => x.id === u.id ? saved : x));
                      } catch (e) {
                        setError(e?.message || 'Failed to update user');
                      }
                    }}>Save</Button>
                    <Button size="small" color="error" variant="outlined" onClick={async () => {
                      try {
                        await apiService.deleteUser(u.id);
                        setUsers(prev => prev.filter(x => x.id !== u.id));
                      } catch (e) {
                        setError(e?.message || 'Failed to delete user');
                      }
                    }}>Delete</Button>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
        </Box>
      </Card>
      )}
      {tab === 'groups' && (
      <Card elevation={3}>
        <Typography variant="h6" sx={{ mb: 2 }}>Create User Group</Typography>
        {gError && <Alert severity="error" sx={{ mb: 2 }}>{gError}</Alert>}
        {gSuccess && <Alert severity="success" sx={{ mb: 2 }}>{gSuccess}</Alert>}
        <Box component="form" onSubmit={handleCreateGroup}>
          <TextField label="Group Name" value={gName} onChange={(e) => setGName(e.target.value)} fullWidth sx={{ mb: 2 }} />
          <TextField label="Description" value={gDesc} onChange={(e) => setGDesc(e.target.value)} fullWidth sx={{ mb: 2 }} />
          <Button type="submit" variant="contained" disabled={gLoading}>
            {gLoading ? <CircularProgress size={20} color="inherit" /> : 'Create Group'}
          </Button>
        </Box>
        <Divider sx={{ my: 3 }} />
        <Typography variant="h6" sx={{ mb: 2 }}>Groups</Typography>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>ID</TableCell>
              <TableCell>Name</TableCell>
              <TableCell>Description</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {(groups || []).map((g) => (
              <TableRow key={g.id}>
                <TableCell>{g.id}</TableCell>
                <TableCell>{g.name}</TableCell>
                <TableCell>{g.description || ''}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>
      )}
      {tab === 'defects' && (
      <Card elevation={3}>
        <Typography variant="h6" sx={{ mb: 2 }}>Defect Types</Typography>
        <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
          <TextField
            label="Add new defect type"
            value={newDefectType}
            onChange={(e) => setNewDefectType(e.target.value)}
            fullWidth
            size="small"
          />
          <TextField
            label="Description"
            value={newDefectDescription}
            onChange={(e) => setNewDefectDescription(e.target.value)}
            fullWidth
            size="small"
          />
          <Button
            variant="contained"
            onClick={() => {
              const v = (newDefectType || '').trim();
              if (!v) return;
              if (defectTypes.find((d) => (d?.name || '').toLowerCase() === v.toLowerCase())) return;
              const next = [...defectTypes, { name: v, description: (newDefectDescription || '').trim() }];
              setDefectTypes(next);
              setNewDefectType('');
              setNewDefectDescription('');
            }}
          >
            Add
          </Button>
        </Box>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ width: 60 }}>#</TableCell>
              <TableCell>Defect Type</TableCell>
              <TableCell>Description</TableCell>
              <TableCell align="right" sx={{ width: 160 }}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {defectTypes.map((dt, idx) => (
              <TableRow key={`${dt?.id || dt?.name || idx}-${idx}`}>
                <TableCell>{idx + 1}</TableCell>
                <TableCell>
                  <TextField
                    value={dt?.name || ''}
                    onChange={(e) => {
                      const v = e.target.value;
                      setDefectTypes((prev) => prev.map((x, i) => i === idx ? { ...x, name: v } : x));
                    }}
                    size="small"
                    fullWidth
                  />
                </TableCell>
                <TableCell>
                  <Typography
                    variant="body2"
                    color={dt?.description ? 'text.primary' : 'text.secondary'}
                    sx={{
                      maxWidth: 520,
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    }}
                    title={dt?.description || ''}
                  >
                    {dt?.description || 'No description'}
                  </Typography>
                </TableCell>
                <TableCell align="right">
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() => openDefectEditor(idx)}
                    sx={{ mr: 1 }}
                  >
                    Edit
                  </Button>
                  <Button
                    size="small"
                    variant="outlined"
                    color="error"
                    onClick={() => {
                      const next = defectTypes.filter((x, i) => i !== idx);
                      setDefectTypes(next);
                    }}
                  >
                    Delete
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        <Box sx={{ mt: 2 }}>
          <Button
            variant="contained"
            onClick={async () => {
              try {
                const resp = await apiService.saveDefectTypes(defectTypes);
                const next = (resp?.items || [])
                  .map((it) => ({
                    id: it?.id,
                    name: String(it?.name || '').trim(),
                    description: String(it?.description || '').trim(),
                  }))
                  .filter((it) => !!it.name);
                setDefectTypes(next);
                setSuccess('Defect types saved');
              } catch (e) {
                setError(e?.message || 'Failed to save defect types');
              }
            }}
          >
            Save Changes
          </Button>
        </Box>
      </Card>
      )}
      <Dialog open={defectEditOpen} onClose={closeDefectEditor} maxWidth="md" fullWidth>
        <DialogTitle>Edit Defect Type</DialogTitle>
        <DialogContent sx={{ pt: 1 }}>
          <TextField
            label="Defect Type"
            value={editingDefectName}
            onChange={(e) => setEditingDefectName(e.target.value)}
            fullWidth
            size="small"
            sx={{ mt: 1, mb: 2 }}
          />
          <TextField
            label="Description"
            value={editingDefectDescription}
            onChange={(e) => setEditingDefectDescription(e.target.value)}
            fullWidth
            multiline
            minRows={8}
            placeholder="Enter the repair methodology or recommendation text to show in Section 5 when this defect is used."
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDefectEditor}>Cancel</Button>
          <Button variant="contained" onClick={applyDefectEditor}>Apply</Button>
        </DialogActions>
      </Dialog>
      {tab === 'annex' && (
      <Card elevation={3}>
        <Typography variant="h6" sx={{ mb: 2 }}>Annex List</Typography>
        <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
          <TextField
            label="Annex Title"
            value={newAnnexTitle}
            onChange={(e) => setNewAnnexTitle(e.target.value)}
            fullWidth
            size="small"
          />
          <Button
            variant="contained"
            onClick={async () => {
              const v = (newAnnexTitle || '').trim();
              if (!v) return;
              if (annexList.find((d) => (d.title || '').toLowerCase() === v.toLowerCase())) return;
              try {
                const created = await apiService.addAnnexMaster(v);
                setAnnexList((prev) => [...prev, created]);
                setNewAnnexTitle('');
              } catch (e) {
                console.error('Failed to add annex title', e);
              }
            }}
          >
            Add
          </Button>
        </Box>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ width: 60 }}>#</TableCell>
              <TableCell>Annex Title</TableCell>
              <TableCell sx={{ width: 220 }}>File</TableCell>
              <TableCell align="right" sx={{ width: 160 }}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {annexList.map((row, idx) => (
              <TableRow key={`${row.id}-${idx}`}>
                <TableCell>{idx + 1}</TableCell>
                <TableCell>{row.title}</TableCell>
                <TableCell>
                  {row.file_url ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {isPdfUrl(row.file_url) ? (
                        <Box
                          sx={{
                            width: 120,
                            height: 64,
                            borderRadius: 1,
                            border: '1px solid #e5e7eb',
                            bgcolor: '#f8fafc',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: 12,
                            fontWeight: 700,
                            color: '#334155',
                          }}
                        >
                          PDF
                        </Box>
                      ) : (
                        <img
                          alt={row.title}
                          src={
                            String(row.file_url).startsWith('/uploads/')
                              ? `${apiService.baseURL || ''}${row.file_url}`
                              : row.file_url
                          }
                          style={{ width: 120, height: 64, objectFit: 'cover', borderRadius: 4, border: '1px solid #e5e7eb' }}
                        />
                      )}
                      <Typography variant="body2" color="text.secondary">
                        {Array.isArray(row.file_urls) ? `${row.file_urls.length} files` : '1 file'}
                      </Typography>
                      <Button
                        size="small"
                        variant="outlined"
                        component="label"
                      >
                        Upload File
                        <input
                          hidden
                          type="file"
                          accept="image/*,.pdf"
                          multiple
                          onChange={async (e) => {
                            const fs = Array.from(e.target.files || []);
                            e.target.value = '';
                            if (fs.length === 0) return;
                            try {
                              const saved = await apiService.uploadAnnexMasterFile(row.id, fs);
                              setAnnexList((prev) => prev.map((x) => x.id === row.id ? { ...x, ...saved } : x));
                              setSuccess('Annex file uploaded');
                            } catch (err) {
                              setError(err?.message || 'Failed to upload annex file');
                            }
                          }}
                        />
                      </Button>
                    </Box>
                  ) : (
                    <Button
                      size="small"
                      variant="outlined"
                      component="label"
                    >
                      Upload File
                      <input
                        hidden
                        type="file"
                        accept="image/*,.pdf"
                        multiple
                        onChange={async (e) => {
                          const fs = Array.from(e.target.files || []);
                          e.target.value = '';
                          if (fs.length === 0) return;
                          try {
                            const saved = await apiService.uploadAnnexMasterFile(row.id, fs);
                            setAnnexList((prev) => prev.map((x) => x.id === row.id ? { ...x, ...saved } : x));
                            setSuccess('Annex file uploaded');
                          } catch (err) {
                            setError(err?.message || 'Failed to upload annex file');
                          }
                        }}
                      />
                    </Button>
                  )}
                </TableCell>
                <TableCell align="right">
                  <Button
                    size="small"
                    variant="outlined"
                    color="error"
                    onClick={async () => {
                      try {
                        await apiService.deleteAnnexMaster(row.id);
                        setAnnexList((prev) => prev.filter((x) => x.id !== row.id));
                      } catch (e) {
                        console.error('Failed to delete annex title', e);
                      }
                    }}
                  >
                    Delete
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        <Box sx={{ mt: 2 }}>
          <Button variant="outlined" disabled>Synced</Button>
        </Box>
      </Card>
      )}
    </Container>
  );
};

export default Settings;
